import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../constants';
import { AuthContext } from '../context/AuthContext'; 

interface Color {
  _id: string;
  name: string;
  firmCode: string;
  tcxCode: string;
}

interface Product {
  _id: string;
  modelName: string;
  category: string;
  subCategory: string;
  modelType: string;
  color: Color;
  fabric: string; // Eksik olan fabric alanını ekledik
  modelImage?: string;
  imageUrl?: string;
  imageUrls?: string;
  description: string;
  isPrototype: boolean;
  prototypeCode?: string;
  productCode?: string; // Bazı ürünlerde olmayabilir, bu yüzden isteğe bağlı
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface ProductListProps {
  showPrototypes: boolean;
}

const ProductList: React.FC<ProductListProps> = ({ showPrototypes }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { hasPermission, isAdmin } = useContext(AuthContext);
  useEffect(() => {
    fetchProducts();
  }, [showPrototypes]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      // API URL'e isPrototype parametresini string olarak ekliyoruz
      // API tarafında bu değer 'true' veya 'false' olarak işlenecek
      const response = await fetch(`${API_URL}/products?isPrototype=${showPrototypes.toString()}`);
      
      if (!response.ok) {
        throw new Error('Ürünler alınırken bir hata oluştu');
      }
      
      const data = await response.json();
      console.log(`${showPrototypes ? 'Prototip' : 'Ürün'} sayısı:`, data.length);
      setProducts(data);
    } catch (err) {
      console.error('Veri çekme hatası:', err);
      setError(err instanceof Error ? err.message : 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const pageTitle = showPrototypes ? 'Prototipler' : 'Ürünler';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">{pageTitle}</h1>
         { hasPermission('products', 'create') || isAdmin ? 
          <button
          onClick={() => navigate('/create-product')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {showPrototypes ? 'Yeni Prototip Oluştur' : 'Yeni Ürün Oluştur'}
        </button>
         : null }
         
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">Yükleniyor...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-xl text-red-600">{error}</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">
              {showPrototypes ? 'Henüz prototip bulunmuyor' : 'Henüz ürün bulunmuyor'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <div
                key={product._id}
                onClick={() => navigate(`/products/${product._id}`)}
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer"
              >
                <div className="relative">
                  {(product.imageUrls || product.modelImage) ? (
                    <img
                      src={`${API_URL}${(product.imageUrls ?? [])[0] || product.modelImage}`}
                      alt={product.modelName}
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-400">Görsel Yok</span>
                    </div>
                  )}

                  <div className="absolute top-2 right-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      product.status === 'active' ? 'bg-green-500 text-white' : 
                      product.status === 'archived' ? 'bg-gray-500 text-white' : 
                      'bg-yellow-500 text-white'
                    }`}>
                      {product.status === 'active' ? 'Aktif' : 
                       product.status === 'archived' ? 'Arşivlendi' : 'Taslak'}
                    </span>
                  </div>
                </div>

                <div className="p-4">
                  <h2 className="text-lg font-semibold text-gray-800 mb-1">{product.modelName}</h2>
                  <p className="text-sm text-gray-600 mb-2">
                    {product.modelType === 'knitting' ? 'Örme' : 'Dokuma'} / {product.fabric}
                  </p>

                  {product.isPrototype ? (
                    <p className="text-xs font-semibold text-purple-600">
                      Prototip Kodu: {product.prototypeCode || 'Belirtilmemiş'}
                    </p>
                  ) : (
                    <p className="text-xs font-semibold text-blue-600">
                      Ürün Kodu: {product.productCode || 'Belirtilmemiş'}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductList;